import type { AnyModelSpec, ConstraintSpec, OrderBySpec } from '@elara/firebolt/shared';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type {
  AnyClientModelInstance,
  ClientBaseInstance,
  ClientCollectionGroupQuery,
  ClientCollectionRef,
  ClientModelInstance,
  ModelExtensions,
  QueryHandle,
  SpecQueryHandle,
} from './types.js';

import { batch } from '@preact/signals-react';
import { createFlatStore } from '@preact-signals/utils';
import { deepSignal } from 'deepsignal/react';

import { makeKey } from '@elara/core/utils';
import { FirestoreCache } from '@elara/firebolt/FireCacheManager';

import { getErrorReporter } from '#lib/error-reporting.js';

import { registerActiveListener } from './listener-registry.js';

/**
 * Creates a reactive array that wraps the provided initial array and enables reactivity.
 * @link https://github.com/luisherranz/deepsignal/blob/main/README.md
 *
 * @param init - The initial array of client instances to create as reactive. Defaults to an empty array if no array is provided.
 * @return A `DeepSignalArray` type is returned, but library is not exposing it. We need to cast it to `ClientModelInstance<Spec, Extensions>[]` to use it.
 */
export function createReactiveArray<Spec extends AnyModelSpec, Extensions extends ModelExtensions<Spec>>(
  init: Array<ClientModelInstance<Spec, Extensions>> = [],
): Array<ClientModelInstance<Spec, Extensions>> {
  return deepSignal(init) as unknown as Array<ClientModelInstance<Spec, Extensions>>;
}

const queryCache = new Map<string, QueryHandle<AnyClientModelInstance>>();
const RETRYABLE_SNAPSHOT_ERROR_CODES = new Set(['permission-denied', 'unauthenticated', 'unavailable']);
const MAX_SNAPSHOT_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY_MS = 300;
const MAX_RETRY_DELAY_MS = 5_000;

function normalizeFirestoreErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('code' in error)) return undefined;
  const code = (error as { code?: unknown }).code;
  if (typeof code !== 'string' || code.length === 0) return undefined;
  return code.includes('/') ? code.slice(code.lastIndexOf('/') + 1) : code;
}

function shouldRetrySnapshotError(error: unknown) {
  const code = normalizeFirestoreErrorCode(error);
  return code !== undefined && RETRYABLE_SNAPSHOT_ERROR_CODES.has(code);
}

/**
 * Extracts Firebase Console index creation URL from a failed-precondition error.
 * Firebase includes a direct link to create the missing composite index in the error message.
 */
function extractMissingIndexUrl(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const message = 'message' in error && typeof (error as { message?: unknown }).message === 'string'
    ? (error as { message: string }).message
    : undefined;
  if (!message) return undefined;
  const match = message.match(/https:\/\/console\.firebase\.google\.com[^\s]+/);
  return match?.[0];
}

/**
 * Logs a prominent error when a Firestore query fails due to a missing composite index.
 * The URL in the error message directly creates the required index when clicked.
 * Also reports to Sentry for production visibility.
 */
function logMissingIndexError(specName: string, cacheKey: string, error: unknown) {
  const indexUrl = extractMissingIndexUrl(error);

  // Report with structured context
  getErrorReporter().captureException(error instanceof Error ? error : new Error('Missing Firestore composite index'), {
    tags: {
      module: 'createClientQuery',
      errorType: 'missing-index',
      spec: specName,
    },
    extra: {
      cacheKey,
      indexUrl: indexUrl ?? 'not-extracted',
      specName,
    },
  });

  // Console logging for local development
  console.error(
    '%c MISSING FIRESTORE INDEX',
    'background: #dc2626; color: white; font-size: 14px; font-weight: bold; padding: 4px 8px; border-radius: 4px;',
  );
  console.error(`Query [${specName}] (key: ${cacheKey}) failed because a composite index is missing.`);
  if (indexUrl) {
    console.error(
      '%c Click to create index: %c' + indexUrl,
      'font-weight: bold;',
      'color: #2563eb; text-decoration: underline;',
    );
  } else {
    console.error('Could not extract index URL from error. Check the full error below for the Firebase Console link.');
  }
  console.error('Full error:', error);
}

/**
 * Creates a live, reactive, and cached query subscription for a Firestore collection.
 *
 * This implementation uses computed signals to ensure stable object identity and proper
 * reactivity when called from getters. The query is lazily initialized and automatically
 * cleaned up when no longer in use.
 *
 * @param firestore - The DI container for Firebase services.
 * @param spec - The `ModelSpec` defining the collection to query.
 * @param collRef - The Firestore collection reference to query.
 * @param extraConstraints - Any additional Firestore query constraints (e.g., `where`, `orderBy`).
 * @returns A reactive query object.
 */
export function createClientQuery<Spec extends AnyModelSpec, Extensions extends ModelExtensions<Spec>>(
  firestore: FirestoreContainer,
  spec: Spec,
  collRef: ClientCollectionRef<Spec, Extensions> | ClientCollectionGroupQuery<Spec, Extensions>,
  extraConstraints: ConstraintSpec[],
): SpecQueryHandle<Spec, Extensions> {
  const constraintsSpecs = [...spec.constraints, ...extraConstraints];
  const queryConstraints = firestore.translateConstraints(constraintsSpecs);
  const orderBySpecs = constraintsSpecs.filter((c) => c.type === 'orderBy');
  const comparator = orderBySpecs.length > 0 ? createClientSideComparator(orderBySpecs) : null;
  const cacheKey = makeKey('path' in collRef ? collRef.path : collRef.$id, constraintsSpecs);

  if (queryCache.has(cacheKey)) {
    return queryCache.get(cacheKey) as SpecQueryHandle<Spec, Extensions>;
  }

  const [modelQuery] = createFlatStore({
    key: cacheKey,
    isReady: false,
    fromCache: true,
    error: undefined,
    items: createReactiveArray(),
    unsubscribe() {},
  } as SpecQueryHandle<Spec, Extensions>);

  const firestoreQuery = firestore.query(collRef, ...queryConstraints);
  let retryAttempt = 0;
  let isDisposed = false;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let stopSnapshotStream: () => void = () => {};

  const clearRetryTimer = () => {
    if (retryTimer !== undefined) {
      clearTimeout(retryTimer);
      retryTimer = undefined;
    }
  };

  const subscribe = () => {
    if (isDisposed) return;
    stopSnapshotStream();
    console.debug(
      `[createClientQuery] Subscribing to query [${spec.name}] (key: ${cacheKey}) (attempt ${retryAttempt + 1}/${MAX_SNAPSHOT_RETRY_ATTEMPTS + 1})`,
      queryConstraints,
    );
    stopSnapshotStream = firestore.onSnapshot(
      firestoreQuery,
      // `includeMetadataChanges` — otherwise a cache→server transition that resolves to the SAME
      // (empty) data never re-invokes this callback, and the "wait for server-confirmed empty" check
      // below would hang forever instead of catching that follow-up confirmation.
      { includeMetadataChanges: true },
      (snapshot) => {
        const recoveredAttempts = retryAttempt;
        clearRetryTimer();
        if (!snapshot.metadata.fromCache) {
          retryAttempt = 0;
        }
        if (recoveredAttempts > 0 && !snapshot.metadata.fromCache) {
          console.info(
            `[createClientQuery] Snapshot recovered for query [${spec.name}] (key: ${cacheKey}) after ${recoveredAttempts} retry attempt(s)`,
          );
        }
        const newValues: Array<ClientModelInstance<Spec, Extensions>> = [];
        for (const doc of snapshot.docs) {
          try {
            newValues.push(doc.data());
          } catch (error) {
            getErrorReporter().captureException(error, {
              tags: { module: 'createClientQuery', spec: spec.name },
              extra: { cacheKey, docPath: doc.ref.path, docId: doc.id },
            });
            console.warn(
              `[createClientQuery] Failed to parse document ${doc.ref.path} in query [${spec.name}] (key: ${cacheKey}):`,
              error,
            );
          }
        }
        batch(() => {
          applyCollectionDiff(modelQuery.items, newValues);
          // An EMPTY snapshot straight from the local cache can mean "nothing cached for this query
          // yet" rather than "confirmed zero matches" — most exposed on mobile's memory-only cache
          // (persistence disabled, so every session starts cold; see firebase-js-sdk#5629/#3053) or
          // right after reconnecting. Trust a non-empty result immediately, matching the desired
          // instant-from-warm-cache UX; for an empty result, wait for server confirmation
          // (`!fromCache`) before marking ready, so a still-loading query reads as loading rather than
          // a false confirmed-empty state (ELARA-738/739).
          if (newValues.length > 0 || !snapshot.metadata.fromCache) {
            modelQuery.isReady = true;
          }
          modelQuery.fromCache = snapshot.metadata.fromCache;
          modelQuery.error = undefined;
          if (comparator && modelQuery.items.length > 0) {
            modelQuery.items.sort(comparator);
          }
        });
      },
      (err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        const errorCode = normalizeFirestoreErrorCode(err);
        const retryable = shouldRetrySnapshotError(err);
        batch(() => {
          console.warn(
            `[createClientQuery] Snapshot error for query [${spec.name}] (key: ${cacheKey}, code: ${errorCode ?? 'unknown'})`,
            error,
          );
          if (!retryable || retryAttempt >= MAX_SNAPSHOT_RETRY_ATTEMPTS) {
            if (!retryable) {
              if (errorCode === 'failed-precondition') {
                logMissingIndexError(spec.name, cacheKey, err);
              } else {
                console.error(
                  `[createClientQuery] Not retrying query [${spec.name}] (key: ${cacheKey}) because the error is non-retryable (code: ${errorCode ?? 'unknown'})`,
                );
              }
            } else {
              console.error(
                `[createClientQuery] Exhausted snapshot retries for query [${spec.name}] (key: ${cacheKey}) after ${retryAttempt} retry attempt(s)`,
              );
            }
            modelQuery.isReady = true;
            modelQuery.error = error;
            return;
          }
          const delayMs = Math.min(INITIAL_RETRY_DELAY_MS * 2 ** retryAttempt, MAX_RETRY_DELAY_MS);
          retryAttempt += 1;
          console.warn(
            `[createClientQuery] Retrying query [${spec.name}] (key: ${cacheKey}) in ${delayMs}ms (retry ${retryAttempt}/${MAX_SNAPSHOT_RETRY_ATTEMPTS}, code: ${errorCode ?? 'unknown'})`,
          );
          modelQuery.isReady = modelQuery.items.length > 0;
          modelQuery.error = error;
          clearRetryTimer();
          retryTimer = setTimeout(() => {
            retryTimer = undefined;
            subscribe();
          }, delayMs);
        });
      },
    );
  };

  subscribe();

  // Re-subscribe on app resume (ELARA-695): mobile suspends sockets on
  // background, leaving onSnapshot stalled without erroring. Resetting the
  // backoff and re-running subscribe() (which stops the prior stream first)
  // forces a fresh connection. Idempotent and a no-op once disposed.
  const unregisterResubscribe = registerActiveListener(() => {
    retryAttempt = 0;
    clearRetryTimer();
    subscribe();
  });

  modelQuery.unsubscribe = function destroyClientQuery() {
    isDisposed = true;
    unregisterResubscribe();
    clearRetryTimer();
    stopSnapshotStream();
    for (const item of modelQuery.items) {
      FirestoreCache.release(item.path);
    }
    queryCache.delete(cacheKey);
  };

  queryCache.set(cacheKey, modelQuery);
  return modelQuery;
}

/** Returns diagnostic information about the query cache for debugging. */
export function getQueryCacheStats(): { size: number; keys: string[] } {
  return {
    size: queryCache.size,
    keys: [...queryCache.keys()],
  };
}

/**
 * Creates a client-side comparator function based on OrderBySpec constraints
 * This ensures client-side sorting matches the server-side ordering rules
 * @param orderBySpecs Array of OrderBySpec constraints from the ModelSpec
 * @returns A comparator function for sorting arrays
 */
function createClientSideComparator<T extends Record<string, unknown>>(
  orderBySpecs: OrderBySpec[],
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    for (const spec of orderBySpecs) {
      const field = spec.field as string;
      let aVal = a[field];
      let bVal = b[field];

      if (field === '__name__') {
        aVal = a.id;
        bVal = b.id;
      }

      if (aVal instanceof Date) aVal = aVal.getTime();
      if (bVal instanceof Date) bVal = bVal.getTime();

      if (aVal == null && bVal == null) continue;
      if (aVal == null) return spec.direction === 'asc' ? -1 : 1;
      if (bVal == null) return spec.direction === 'asc' ? 1 : -1;

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }

      if (comparison !== 0) {
        return spec.direction === 'asc' ? comparison : -comparison;
      }
    }
    return 0;
  };
}

/**
 * @internal
 * @function applyCollectionDiff
 * Efficiently updates a local array of model instances with a new array from a Firestore snapshot.
 * It correctly handles additions, removals, and replacements, and calls `FirestoreCache.release` for
 * instances that are no longer part of the result set.
 * Note: This function should be called within a batch() to prevent multiple re-renders.
 * @param oldValues - The current local array (will be mutated).
 * @param newValues - The new array from the snapshot.
 */
function applyCollectionDiff(
  oldValues: Array<ClientBaseInstance<AnyModelSpec>>,
  newValues: Array<ClientBaseInstance<AnyModelSpec>>,
) {
  for (let i = 0; i < newValues.length; i++) {
    if (oldValues[i] === undefined) {
      oldValues[i] = newValues[i]!;
    } else {
      if (oldValues[i] !== newValues[i]) {
        FirestoreCache.release(oldValues[i]!.path);
        oldValues[i] = newValues[i]!;
      }
    }
  }
  if (oldValues.length > newValues.length) {
    for (let i = newValues.length; i < oldValues.length; i++) {
      const value = oldValues[i];
      if (value) FirestoreCache.release(value.path);
    }
    oldValues.splice(newValues.length);
  }
}
